import React, { useContext } from 'react';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import OpacityIcon from '@mui/icons-material/Opacity';
import { ColorPickerPopover } from '../ColorPickerPopover';
import { I18nReactContext } from '../../../../../../contexts/I18nContext';

/**
 * Appearance section with border radius, background color, and opacity
 * Configurable to show/hide specific controls based on component type
 * @param {object} props - Component props
 * @param {object} props.component - The component being edited
 * @param {function} props.onContentChange - Callback when content changes
 * @param {object} props.config - Section configuration
 * @param {boolean} props.config.showBorderRadius - Show border radius control
 * @param {boolean} props.config.showBackgroundColor - Show background color control
 * @param {boolean} props.config.showOpacity - Show opacity control
 * @param {boolean} props.config.showBorderWidth - Show border width control
 * @param {boolean} props.config.showBorderColor - Show border color control
 * @param {boolean} props.config.hideCommonBackgroundColor - Hide common background color (for shapes that have their own)
 */
export const AppearanceSection = ({ component, onContentChange, config = {} }) => {
    const { i18n } = useContext(I18nReactContext);

    const {
        showBorderRadius = true,
        showBackgroundColor = true,
        showOpacity = true,
        showBorderWidth = false,
        showBorderColor = false,
        hideCommonBackgroundColor = false
    } = config;

    // For shapes, we hide the common background color section since ShapeTypeSection handles it
    const shouldShowBackgroundColor = showBackgroundColor && !hideCommonBackgroundColor;

    // Check if the style section should be shown (border controls + background color)
    const showStyleSection = showBorderRadius || showBorderWidth || showBorderColor || shouldShowBackgroundColor;

    return (
        <>
            {/* Style Section - Groups background color, border radius, width, and color */}
            {showStyleSection && (
                <div className="menu-section">
                    <h4>{i18n.translate('weezbeeCourse.editor.editorDrawer.style', { defaultValue: 'Style' })}</h4>
                    <div className="border-controls-group">
                        {/* Background Color */}
                        {shouldShowBackgroundColor && (
                            <div className="border-control-row">
                                <label>{i18n.translate('weezbeeCourse.editor.editorDrawer.background', { defaultValue: 'Background' })}</label>
                                <ColorPickerPopover
                                    color={component.backgroundColor || 'transparent'}
                                    onChange={(color) => onContentChange(component.id, undefined, { backgroundColor: color })}
                                    icon={<FormatColorFillIcon fontSize="small" />}
                                    title={i18n.translate('weezbeeCourse.editor.component.backgroundColor', { defaultValue: 'Background Color' })}
                                    showTransparent
                                />
                            </div>
                        )}

                        {/* Border Radius */}
                        {showBorderRadius && (
                            <div className="border-control-row">
                                <label>{i18n.translate('weezbeeCourse.editor.component.borderRadius', { defaultValue: 'Radius' })}</label>
                                <div className="border-radius-controls">
                                    <div className="border-radius-input">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={(component.borderRadius && component.borderRadius.topLeft !== undefined) ? component.borderRadius.topLeft : 0}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value, 10);
                                                const radius = isNaN(value) ? 0 : value;
                                                const newBorderRadius = {
                                                    topLeft: radius,
                                                    topRight: radius,
                                                    bottomLeft: radius,
                                                    bottomRight: radius
                                                };
                                                onContentChange(component.id, undefined, { borderRadius: newBorderRadius });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Border Width */}
                        {showBorderWidth && (
                            <div className="border-control-row">
                                <label>{i18n.translate('weezbeeCourse.editor.editorDrawer.borderWidth', { defaultValue: 'Width' })}</label>
                                <div className="border-radius-controls">
                                    <div className="border-radius-input">
                                        <input
                                            type="number"
                                            min="0"
                                            max="20"
                                            value={component.borderWidth || 0}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value, 10);
                                                const width = isNaN(value) ? 0 : value;
                                                onContentChange(component.id, undefined, { borderWidth: width });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Border Color */}
                        {showBorderColor && (
                            <div className="border-control-row">
                                <label>{i18n.translate('weezbeeCourse.editor.editorDrawer.borderColor', { defaultValue: 'Color' })}</label>
                                <ColorPickerPopover
                                    color={component.borderColor || '#000000'}
                                    onChange={(color) => onContentChange(component.id, undefined, { borderColor: color })}
                                    title={i18n.translate('weezbeeCourse.editor.editorDrawer.borderColor', { defaultValue: 'Border Color' })}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Opacity */}
            {showOpacity && (
                <div className="menu-section">
                    <h4>{i18n.translate('weezbeeCourse.editor.component.opacity', { defaultValue: 'Opacity' })}</h4>
                    <div className="opacity-slider">
                        <OpacityIcon fontSize="small" />
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={(component.opacity !== undefined ? component.opacity : 1) * 100}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                const opacity = value / 100;
                                onContentChange(component.id, undefined, { opacity });
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span>{Math.round((component.opacity !== undefined ? component.opacity : 1) * 100)}{i18n.translate('weezbeeCourse.editor.component.percentUnit', { defaultValue: '%' })}</span>
                    </div>
                </div>
            )}
        </>
    );
};
